import { useTranslation } from 'react-i18next';
import { ReactSortable } from 'react-sortablejs';
import { Text } from '@mantine/core';
import clsx from 'clsx';

import {
  MAX_GROUPED_COLUMNS,
  SORTABLE_GROUPING_SECTION_CLASS_NAME,
} from '../../utils/constants';
import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { SVGIcon } from '../icons/SVGIcon';

import { TableColumnSettings } from './TableCustomizationModal';

import styles from './table-customization.module.scss';

type TableCustomizationGroupedListProps = {
  items: TableColumnSettings[];
  onChange: (newState: TableColumnSettings[]) => void;
};

type TableCustomizationGroupedItemProps = TableColumnSettings & {
  slot: number;
};

export function TableCustomizationGroupedList({
  items,
  onChange,
}: TableCustomizationGroupedListProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className={styles['grouping-section-header']}>
        {t('app.table.customizationModal.groupingHeader', {
          maxColumns: MAX_GROUPED_COLUMNS,
        })}
        <br />
        {t('app.table.customizationModal.groupingNote')}
      </div>
      <ReactSortable
        group="columnCustomization"
        className={clsx(
          styles['sortable'],
          SORTABLE_GROUPING_SECTION_CLASS_NAME
        )}
        list={items}
        setList={onChange}
      >
        {items.map((item, idx) => (
          <TableCustomizationGroupedItem
            {...item}
            key={item.id}
            slot={idx + 1}
          />
        ))}
      </ReactSortable>
    </>
  );
}

function TableCustomizationGroupedItem({
  isGroupable,
  label,
  sublabel,
  slot,
}: TableCustomizationGroupedItemProps) {
  return (
    <div className={clsx(styles['item'], styles['item--sortable'])}>
      <div
        style={{ marginRight: `${slot * 16}px` }}
        className={styles['item__left']}
      >
        {slot}
      </div>
      <div className={styles['item__right']}>
        <Text classNames={{ root: styles['item__text'] }}>
          {label}
          {sublabel && (
            <span className={styles['item__text__sublabel']}>{sublabel}</span>
          )}
        </Text>
        {isGroupable && (
          <Button
            disabled
            icon={<SVGIcon name="groupBy" />}
            title="Group by"
            variant="text"
          />
        )}
        <GoogleFontIcon
          className={styles['item__reorder-symbol']}
          name="reorder"
        />
      </div>
    </div>
  );
}
